import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Field, Input } from "@/components/ui/input";
import { useT } from "@/i18n";
import { useDocumentTitle } from "@/app/useDocumentTitle";
import { api } from "@/lib/api";

/**
 * Self-service password reset.
 *
 * The confirmation is identical whether or not the address belongs to an account. On an
 * invite-only system, saying "no such user" would turn this page into a way to discover
 * who works here.
 */
export function ForgotPasswordPage(): React.JSX.Element {
  const t = useT();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useDocumentTitle(t("forgot.submit"));

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    // Deliberately ignores the outcome: the API always answers the same way, and so do we.
    await api.post("/auth/forgot-password", { email }).catch(() => undefined);
    setSent(true);
    setSubmitting(false);
  };

  return (
    <main className="brand-bloom-page flex min-h-dvh w-full items-center justify-center p-4 sm:p-8">
      <div className="bg-card ring-border/60 w-full max-w-sm space-y-6 rounded-xl p-6 shadow-2xl ring-1 sm:p-8">
        <header className="space-y-3">
          <div className="flex items-center gap-3">
            <img
              src="/infa-partner-logo.png"
              alt={t("app.logoAlt")}
              width={44}
              height={44}
              className="h-11 w-11 shrink-0 rounded-sm object-contain"
            />
            <h1 className="text-2xl font-semibold tracking-tight">{t("app.name")}</h1>
          </div>
          <p className="text-muted-foreground text-sm">
            {sent ? t("forgot.sentLead") : t("forgot.lead")}
          </p>
        </header>

        {sent ? (
          <div className="space-y-4">
            <p className="text-sm">
              {t("forgot.sentPrefix")} <span className="font-medium">{email}</span> {t("forgot.sentSuffix")}
            </p>
            <Link to="/login" className="text-primary text-sm underline">
              {t("setPassword.backToSignIn")}
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" noValidate>
            <Field label={t("auth.email")} htmlFor="forgot-email">
              <Input
                id="forgot-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <Button type="submit" disabled={submitting || !email} className="w-full">
              {submitting ? t("forgot.sending") : t("forgot.submit")}
            </Button>

            <Link to="/login" className="text-muted-foreground block text-center text-sm underline">
              {t("setPassword.backToSignIn")}
            </Link>
          </form>
        )}
      </div>
    </main>
  );
}
